import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Capacitor } from '@capacitor/core'
import {
  ChevronLeft,
  PenLine,
  Smartphone,
  Loader2,
  CheckCircle2,
  XCircle,
  Users,
} from 'lucide-react'

function LinkedInIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  )
}
function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.657l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  )
}
function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  )
}
import { cn } from '@/lib/utils'
import {
  useBackgroundImportLinkedIn,
  useBackgroundImportInstagram,
  useBackgroundImportX,
  useBulkImportLinkedIn,
} from '@/hooks/use-contacts'
import type { BulkImportJob } from '@/hooks/use-contacts'

export default function ContactCreateMethodPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const backgroundImport = useBackgroundImportLinkedIn()
  const backgroundImportInstagram = useBackgroundImportInstagram()
  const backgroundImportX = useBackgroundImportX()
  const bulkImport = useBulkImportLinkedIn()

  const [showLinkedInInput, setShowLinkedInInput] = useState(false)
  const [showInstagramInput, setShowInstagramInput] = useState(false)
  const [showXInput, setShowXInput] = useState(false)
  const [showBulkInput, setShowBulkInput] = useState(false)
  const [linkedInUrl, setLinkedInUrl] = useState('')
  const [instagramInput, setInstagramInput] = useState('')
  const [xInput, setXInput] = useState('')
  const [bulkUrls, setBulkUrls] = useState('')
  const [urlError, setUrlError] = useState<string | null>(null)
  const [instagramError, setInstagramError] = useState<string | null>(null)
  const [xError, setXError] = useState<string | null>(null)
  const [bulkResults, setBulkResults] = useState<BulkImportJob[] | null>(null)

  const isNative = Capacitor.isNativePlatform()

  function validateLinkedInUrl(url: string): boolean {
    return /(\w+\.)?linkedin\.com\/in\/[\w\u00C0-\u024F\u1E00-\u1EFF%.-]+/i.test(url)
  }

  // Mirrors server/src/lib/x-profile.ts#parseXHandle so the client accepts the
  // exact same forms: https://x.com/malikules, @malikules, malikules.
  function parseXHandle(raw: string): string | null {
    let value = raw.trim()
    if (!value) return null
    const urlMatch = value.match(/(?:https?:\/\/)?(?:www\.)?(?:x|twitter)\.com\/([^/?#]+)/i)
    if (urlMatch) value = urlMatch[1]
    value = value.replace(/^@+/, '').split(/[/?#]/)[0].trim()
    return /^[A-Za-z0-9_]{1,15}$/.test(value) ? value : null
  }

  function handleLinkedInSubmit() {
    if (!validateLinkedInUrl(linkedInUrl)) {
      setUrlError(t('contacts.linkedInInvalidUrl'))
      return
    }
    setUrlError(null)
    backgroundImport(linkedInUrl)
    navigate(-1)
  }

  function handleXSubmit() {
    if (!parseXHandle(xInput)) {
      setXError(t('contacts.xInvalid'))
      return
    }
    setXError(null)
    backgroundImportX(xInput.trim())
    navigate(-1)
  }

  // Accepts a full instagram.com profile URL or a bare handle (with/without @).
  function validateInstagramInput(value: string): boolean {
    const v = value.trim()
    if (/instagram\.com\//i.test(v)) {
      const m = v.match(/instagram\.com\/([^/?#]+)/i)
      const handle = m?.[1]?.replace(/^@+/, '') ?? ''
      return /^[A-Za-z0-9._]{1,30}$/.test(handle)
    }
    return /^@?[A-Za-z0-9._]{1,30}$/.test(v)
  }

  function handleInstagramSubmit() {
    if (!validateInstagramInput(instagramInput)) {
      setInstagramError(t('contacts.instagramInvalid'))
      return
    }
    setInstagramError(null)
    backgroundImportInstagram(instagramInput.trim())
    navigate(-1)
  }

  function handleBulkSubmit() {
    const urls = bulkUrls
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)

    if (urls.length === 0) return

    setBulkResults(null)
    bulkImport.mutate(urls, {
      onSuccess: (result) => {
        setBulkResults(result.jobs)
      },
      onError: () => {
        setUrlError(t('contacts.linkedInError'))
      },
    })
  }

  const bulkUrlCount = bulkUrls
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean).length

  return (
    <div className="flex flex-1 flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3">
        <button
          onClick={() => navigate(-1)}
          className="hover:bg-surface-container-highest flex size-9 items-center justify-center rounded-full transition-colors"
        >
          <ChevronLeft className="size-5" />
        </button>
        <h1 className="font-heading text-lg font-semibold">{t('contacts.createMethod')}</h1>
      </div>

      {/* Method cards */}
      <div className="flex flex-col gap-3 px-6 pt-2">
        {/* LinkedIn */}
        <button
          onClick={() => {
            if (!showLinkedInInput) {
              setShowLinkedInInput(true)
              setShowXInput(false)
              setShowInstagramInput(false)
              setShowBulkInput(false)
              setUrlError(null)
              setBulkResults(null)
            }
          }}
          className={cn(
            'bg-surface-container-low flex items-center gap-4 rounded-xl p-4 text-left transition-colors',
            'active:bg-surface-container-highest',
            showLinkedInInput && 'ring-primary ring-2',
          )}
        >
          <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-[#0A66C2]/10">
            <LinkedInIcon className="size-5 text-[#0A66C2]" />
          </div>
          <div>
            <p className="font-semibold">{t('contacts.methodLinkedIn')}</p>
            <p className="text-on-surface-variant text-sm">{t('contacts.methodLinkedInDesc')}</p>
          </div>
        </button>

        {/* LinkedIn URL input */}
        {showLinkedInInput && (
          <div className="bg-surface-container-low animate-in fade-in slide-in-from-top-1 -mt-1 space-y-3 rounded-xl p-4 duration-200">
            <input
              type="url"
              autoFocus
              value={linkedInUrl}
              onChange={(e) => {
                setLinkedInUrl(e.target.value)
                setUrlError(null)
              }}
              placeholder={t('contacts.linkedInUrlPlaceholder')}
              className="border-outline-variant bg-surface focus:border-primary focus:ring-primary w-full rounded-lg border px-3 py-2.5 text-sm outline-none focus:ring-1"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleLinkedInSubmit()
              }}
            />
            {urlError && !showBulkInput && <p className="text-error text-sm">{urlError}</p>}
            <button
              onClick={handleLinkedInSubmit}
              disabled={!linkedInUrl.trim()}
              className={cn(
                'bg-primary text-primary-foreground flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium',
                'disabled:cursor-not-allowed disabled:opacity-50',
              )}
            >
              {t('contacts.linkedInImportButton')}
            </button>
          </div>
        )}

        {/* X */}
        <button
          onClick={() => {
            if (!showXInput) {
              setShowXInput(true)
              setShowLinkedInInput(false)
              setShowInstagramInput(false)
              setShowBulkInput(false)
              setXError(null)
            }
          }}
          className={cn(
            'bg-surface-container-low flex items-center gap-4 rounded-xl p-4 text-left transition-colors',
            'active:bg-surface-container-highest',
            showXInput && 'ring-primary ring-2',
          )}
        >
          <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-black/10 dark:bg-white/10">
            <XIcon className="size-5" />
          </div>
          <div>
            <p className="font-semibold">{t('contacts.methodX')}</p>
            <p className="text-on-surface-variant text-sm">{t('contacts.methodXDesc')}</p>
          </div>
        </button>

        {showXInput && (
          <div className="bg-surface-container-low animate-in fade-in slide-in-from-top-1 -mt-1 space-y-3 rounded-xl p-4 duration-200">
            <input
              type="text"
              autoFocus
              value={xInput}
              onChange={(e) => {
                setXInput(e.target.value)
                setXError(null)
              }}
              placeholder={t('contacts.xPlaceholder')}
              className="border-outline-variant bg-surface focus:border-primary focus:ring-primary w-full rounded-lg border px-3 py-2.5 text-sm outline-none focus:ring-1"
              onKeyDown={(e) => e.key === 'Enter' && handleXSubmit()}
            />
            {xError && <p className="text-error text-sm">{xError}</p>}
            <button
              onClick={handleXSubmit}
              disabled={!xInput.trim()}
              className="bg-primary text-primary-foreground flex w-full items-center justify-center rounded-lg px-4 py-2.5 text-sm font-medium disabled:opacity-50"
            >
              {t('contacts.xImportButton')}
            </button>
          </div>
        )}

        {/* Instagram */}
        <button
          onClick={() => {
            if (!showInstagramInput) {
              setShowInstagramInput(true)
              setShowXInput(false)
              setShowLinkedInInput(false)
              setShowBulkInput(false)
              setInstagramError(null)
              setBulkResults(null)
            }
          }}
          className={cn(
            'bg-surface-container-low flex items-center gap-4 rounded-xl p-4 text-left transition-colors',
            'active:bg-surface-container-highest',
            showInstagramInput && 'ring-primary ring-2',
          )}
        >
          <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-[#E1306C]/10">
            <InstagramIcon className="size-5 text-[#E1306C]" />
          </div>
          <div>
            <p className="font-semibold">{t('contacts.methodInstagram')}</p>
            <p className="text-on-surface-variant text-sm">{t('contacts.methodInstagramDesc')}</p>
          </div>
        </button>

        {/* Instagram input */}
        {showInstagramInput && (
          <div className="bg-surface-container-low animate-in fade-in slide-in-from-top-1 -mt-1 space-y-3 rounded-xl p-4 duration-200">
            <input
              type="text"
              autoFocus
              value={instagramInput}
              onChange={(e) => {
                setInstagramInput(e.target.value)
                setInstagramError(null)
              }}
              placeholder={t('contacts.instagramPlaceholder')}
              className="border-outline-variant bg-surface focus:border-primary focus:ring-primary w-full rounded-lg border px-3 py-2.5 text-sm outline-none focus:ring-1"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleInstagramSubmit()
              }}
            />
            {instagramError && <p className="text-error text-sm">{instagramError}</p>}
            <button
              onClick={handleInstagramSubmit}
              disabled={!instagramInput.trim()}
              className={cn(
                'bg-primary text-primary-foreground flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium',
                'disabled:cursor-not-allowed disabled:opacity-50',
              )}
            >
              {t('contacts.instagramImportButton')}
            </button>
          </div>
        )}

        {/* Bulk LinkedIn Import */}
        <button
          onClick={() => {
            if (!showBulkInput) {
              setShowBulkInput(true)
              setShowXInput(false)
              setShowLinkedInInput(false)
              setShowInstagramInput(false)
              setUrlError(null)
              setBulkResults(null)
            }
          }}
          className={cn(
            'bg-surface-container-low flex items-center gap-4 rounded-xl p-4 text-left transition-colors',
            'active:bg-surface-container-highest',
            showBulkInput && 'ring-primary ring-2',
          )}
        >
          <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-[#0A66C2]/10">
            <Users className="size-5 text-[#0A66C2]" />
          </div>
          <div>
            <p className="font-semibold">{t('contacts.methodBulkLinkedIn')}</p>
            <p className="text-on-surface-variant text-sm">
              {t('contacts.methodBulkLinkedInDesc')}
            </p>
          </div>
        </button>

        {/* Bulk LinkedIn URL input */}
        {showBulkInput && (
          <div className="bg-surface-container-low animate-in fade-in slide-in-from-top-1 -mt-1 space-y-3 rounded-xl p-4 duration-200">
            <textarea
              autoFocus
              value={bulkUrls}
              onChange={(e) => {
                setBulkUrls(e.target.value)
                setUrlError(null)
                setBulkResults(null)
              }}
              placeholder={t('contacts.bulkLinkedInPlaceholder')}
              rows={5}
              className="border-outline-variant bg-surface focus:border-primary focus:ring-primary w-full resize-none rounded-lg border px-3 py-2.5 text-sm outline-none focus:ring-1"
            />
            {bulkUrlCount > 0 && (
              <p className="text-on-surface-variant text-xs">
                {t('contacts.bulkLinkedInCount', { count: bulkUrlCount })}
              </p>
            )}
            {urlError && showBulkInput && <p className="text-error text-sm">{urlError}</p>}
            <button
              onClick={handleBulkSubmit}
              disabled={bulkImport.isPending || bulkUrlCount === 0}
              className={cn(
                'bg-primary text-primary-foreground flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium',
                'disabled:cursor-not-allowed disabled:opacity-50',
              )}
            >
              {bulkImport.isPending ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  {t('contacts.bulkLinkedInImporting')}
                </>
              ) : (
                t('contacts.bulkLinkedInImportButton', { count: bulkUrlCount })
              )}
            </button>

            {/* Bulk import results */}
            {bulkResults && (
              <div className="space-y-2 pt-2">
                <p className="text-sm font-medium">
                  {t('contacts.bulkLinkedInResults', {
                    success: bulkResults.filter((j) => j.status === 'completed').length,
                    total: bulkResults.length,
                  })}
                </p>
                <div className="max-h-48 space-y-1.5 overflow-y-auto">
                  {bulkResults.map((job, i) => (
                    <div
                      key={job.jobId ?? i}
                      className="bg-surface flex items-start gap-2 rounded-lg px-3 py-2 text-sm"
                    >
                      {job.status === 'completed' ? (
                        <CheckCircle2 className="text-chartreuse-foreground dark:text-chartreuse mt-0.5 size-4 shrink-0" />
                      ) : (
                        <XCircle className="text-destructive mt-0.5 size-4 shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate">{job.profileUrl}</p>
                        {job.status === 'completed' && job.contact && (
                          <button
                            onClick={() =>
                              navigate(`/contacts/${(job.contact as { id: string }).id}`)
                            }
                            className="text-primary text-xs hover:underline"
                          >
                            {t('contacts.bulkLinkedInViewContact')}
                          </button>
                        )}
                        {job.status === 'failed' && job.error && (
                          <p className="text-error text-xs">{job.error}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Manual */}
        <button
          onClick={() => navigate('/contacts/new/manual')}
          className="bg-surface-container-low active:bg-surface-container-highest flex items-center gap-4 rounded-xl p-4 text-left transition-colors"
        >
          <div className="bg-primary/10 flex size-11 shrink-0 items-center justify-center rounded-full">
            <PenLine className="text-primary size-5" />
          </div>
          <div>
            <p className="font-semibold">{t('contacts.methodManual')}</p>
            <p className="text-on-surface-variant text-sm">{t('contacts.methodManualDesc')}</p>
          </div>
        </button>

        {/* Phone Import — native only */}
        {isNative && (
          <button
            onClick={() => navigate('/import-contacts')}
            className="bg-surface-container-low active:bg-surface-container-highest flex items-center gap-4 rounded-xl p-4 text-left transition-colors"
          >
            <div className="bg-primary/10 flex size-11 shrink-0 items-center justify-center rounded-full">
              <Smartphone className="text-primary size-5" />
            </div>
            <div>
              <p className="font-semibold">{t('contacts.methodPhone')}</p>
              <p className="text-on-surface-variant text-sm">{t('contacts.methodPhoneDesc')}</p>
            </div>
          </button>
        )}
      </div>
    </div>
  )
}
